import { Link } from "react-router-dom";
import { Calendar, Star, ChevronRight } from "lucide-react";
import type { DailyJournal } from "@/types";

interface JournalListProps {
  journals: DailyJournal[];
}

/**
 * List view for browsing journals chronologically
 *
 * @param journals - List of journals to display
 * @returns Scrollable list of journal entries
 */
export function JournalList({ journals }: JournalListProps) {
  // Group journals by month
  const groupedJournals = journals.reduce((groups, journal) => {
    const date = new Date(journal.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    if (!groups[monthKey]) {
      groups[monthKey] = [];
    }
    groups[monthKey].push(journal);
    return groups;
  }, {} as Record<string, DailyJournal[]>);

  // Format month header
  const formatMonthHeader = (monthKey: string) => {
    const [year, month] = monthKey.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "long",
    });
  };

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("ja-JP", {
      month: "short",
      day: "numeric",
      weekday: "short",
    });
  };

  // Truncate text
  const truncate = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).trim() + "...";
  };

  return (
    <div className="space-y-8">
      {Object.entries(groupedJournals)
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([monthKey, monthJournals]) => (
          <div key={monthKey}>
            <h3 className="text-lg font-display font-semibold text-primary-950 mb-4">
              {formatMonthHeader(monthKey)}
            </h3>

            <div className="space-y-3">
              {monthJournals
                .sort((a, b) => b.date.localeCompare(a.date))
                .map((journal) => (
                  <Link
                    key={journal.id}
                    to={`/journals/${journal.date}`}
                    className="block bg-white rounded-xl border p-5 hover:shadow-md hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="flex items-start gap-4">
                      <div className="shrink-0">
                        <div className="flex items-center gap-2 text-slate-500 mb-1">
                          <Calendar size={16} />
                          <span className="text-sm font-medium">
                            {formatDate(journal.date)}
                          </span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              size={14}
                              className={
                                i < journal.importanceScore
                                  ? "text-accent-500 fill-accent-500"
                                  : "text-slate-200"
                              }
                            />
                          ))}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-slate-700 leading-relaxed">
                          {truncate(journal.overallSummary)}
                        </p>
                        {journal.nextSteps && (
                          <p className="text-sm text-slate-500 mt-2">
                            Next: {truncate(journal.nextSteps, 60)}
                          </p>
                        )}
                      </div>

                      <ChevronRight
                        size={20}
                        className="shrink-0 text-slate-300"
                      />
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        ))}
    </div>
  );
}

